import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { WizardState } from "./types";

interface Props {
  state: WizardState;
  onSubmit: (email: string, password: string) => Promise<void>;
  onBack: () => void;
  loading: boolean;
}

export default function StepAccount({ state, onSubmit, onBack, loading }: Props) {
  const [email, setEmail] = useState(state.email);
  const [password, setPassword] = useState(state.password);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const validate = () => {
    const e: typeof errors = {};
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      e.email = "Bitte gib eine gültige E-Mail-Adresse ein";
    }
    if (!password || password.length < 6) {
      e.password = "Das Passwort muss mindestens 6 Zeichen lang sein";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(email, password);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">
          Fast geschafft
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Noch Email und Passwort, dann legen wir los.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="signup-email">E-Mail</Label>
          <Input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="deine@email.de"
            className="mt-1"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-destructive">{errors.email}</p>
          )}
        </div>
        <div>
          <Label htmlFor="signup-password">Passwort</Label>
          <Input
            id="signup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mindestens 6 Zeichen"
            className="mt-1"
          />
          {errors.password && (
            <p className="mt-1 text-sm text-destructive">{errors.password}</p>
          )}
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onBack} className="flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" /> Zurück
          </Button>
          <Button
            type="submit"
            variant="hero"
            className="flex-1"
            disabled={loading}
          >
            {loading ? "Wird erstellt..." : "Account erstellen"}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Mit der Registrierung akzeptierst du unsere{" "}
          <a href="/agb" target="_blank" rel="noreferrer" className="underline">AGB</a> und{" "}
          <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline">Datenschutzerklärung</a>.
        </p>
      </form>
    </div>
  );
}
